import type { CategoryBudgetStatus } from "@boo/shared";
import { CATEGORY_LABELS } from "../../monthly-summary/types/category.types";
import {
  getBudgetThresholdState,
  thresholdBarClass,
  thresholdTextClass,
} from "../lib/budgetThreshold";

interface Props {
  item: CategoryBudgetStatus;
  projectedAmount?: number;
  monthsOfHistory?: number;
}

export default function CategoryStatusRow({
  item,
  projectedAmount,
  monthsOfHistory,
}: Props) {
  const { category, budgetAmount, spent, remaining } = item;
  const overProjected =
    projectedAmount !== undefined && projectedAmount > budgetAmount;
  const state = getBudgetThresholdState(spent, budgetAmount);
  const pct =
    budgetAmount > 0 ? Math.min((spent / budgetAmount) * 100, 100) : 0;
  const fmt = (n: number) =>
    n.toLocaleString("th-TH", { minimumFractionDigits: 0 });

  const hasProjection = projectedAmount !== undefined;
  const lowConfidence = hasProjection && monthsOfHistory === 1;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-[13px]">
        <span className="font-medium text-foreground">
          {CATEGORY_LABELS[category]}
        </span>
        <div className="flex items-center gap-2 [font-variant-numeric:tabular-nums]">
          <span className="text-muted-foreground">฿{fmt(spent)}</span>
          <span className="text-muted-foreground">/</span>
          <span className="text-foreground">฿{fmt(budgetAmount)}</span>
        </div>
      </div>

      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${thresholdBarClass(state)}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex items-center justify-between">
        {!hasProjection ? (
          <span className="text-[11px] text-muted-foreground italic">
            Not enough data yet
          </span>
        ) : overProjected ? (
          <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400 [font-variant-numeric:tabular-nums]">
            ⚠ Projected ฿{fmt(projectedAmount!)} — heading over
            {lowConfidence && (
              <span className="ml-1 opacity-70">(low confidence)</span>
            )}
          </span>
        ) : (
          <span className="text-[11px] text-muted-foreground [font-variant-numeric:tabular-nums]">
            Projected ฿{fmt(projectedAmount)}
            {lowConfidence && (
              <span className="ml-1 italic">(low confidence)</span>
            )}
          </span>
        )}
        <span
          className={`text-[12px] font-medium [font-variant-numeric:tabular-nums] ${thresholdTextClass(state)}`}
        >
          {remaining >= 0
            ? `฿${fmt(remaining)} left`
            : `฿${fmt(Math.abs(remaining))} over`}
        </span>
      </div>
    </div>
  );
}
